/**
 * Supabase ReportGroups Service
 * Handles all report group-related operations with Supabase
 */

import { supabase } from './client';

export interface SupabaseReportGroup {
  id: string;
  legacy_id: string;
  group_name: string | null;
  description: string | null;
  icon: string | null;
  unit_id: string | null;
  comp_id: string | null;
  user_ids: string | null;
  stt: string | null;
  sync_source: string;
  synced_at: string;
  created_at: string;
  updated_at: string;
}

export interface ReportGroupFilters {
  group_name?: string;
  unit_id?: string;
  comp_id?: string;
  legacy_id?: string;
  search?: string;
}

export interface PaginationOptions {
  page?: number;
  limit?: number;
}

export interface ReportGroupResponse {
  data: SupabaseReportGroup[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

class ReportGroupService {
  /**
   * Get report groups with filtering and pagination
   */
  async getReportGroups(
    filters: ReportGroupFilters = {},
    pagination: PaginationOptions = {}
  ): Promise<ReportGroupResponse> {
    const { page = 1, limit = 50 } = pagination;
    const offset = (page - 1) * limit;

    let query = supabase
      .from('report_groups')
      .select('*', { count: 'exact' });

    // Apply filters
    if (filters.group_name) {
      query = query.ilike('group_name', `%${filters.group_name}%`);
    }

    if (filters.unit_id) {
      query = query.eq('unit_id', filters.unit_id);
    }

    if (filters.comp_id) {
      query = query.eq('comp_id', filters.comp_id);
    }

    if (filters.legacy_id) {
      query = query.eq('legacy_id', filters.legacy_id);
    }

    if (filters.search) {
      query = query.or(`group_name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
    }

    // Apply pagination and ordering
    query = query
      .order('group_name', { ascending: true })
      .range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      throw new Error(`Failed to fetch report groups: ${error.message}`);
    }

    const totalPages = Math.ceil((count || 0) / limit);

    return {
      data: data || [],
      count: count || 0,
      page,
      limit,
      totalPages,
      hasNextPage: page < totalPages,
      hasPrevPage: page > 1,
    };
  }

  /**
   * Get a single report group by ID
   */
  async getReportGroupById(id: string): Promise<SupabaseReportGroup | null> {
    const { data, error } = await supabase
      .from('report_groups')
      .select('*')
      .eq('id', id)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw new Error(`Failed to fetch report group: ${error.message}`);
    }

    return data;
  }

  /**
   * Get a single report group by legacy ID (SQL Server ID)
   */
  async getReportGroupByLegacyId(legacyId: string): Promise<SupabaseReportGroup | null> {
    const { data, error } = await supabase
      .from('report_groups')
      .select('*')
      .eq('legacy_id', legacyId)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw new Error(`Failed to fetch report group: ${error.message}`);
    }

    return data;
  }

  /**
   * Get report group statistics
   */
  async getReportGroupStats(): Promise<{
    totalGroups: number;
    groupsWithIcons: number;
    groupsByCompany: { [compId: string]: number };
  }> {
    const { data, error } = await supabase
      .from('report_groups')
      .select('id, icon, comp_id');

    if (error) {
      throw new Error(`Failed to fetch report group stats: ${error.message}`);
    }

    const stats = {
      totalGroups: data?.length || 0,
      groupsWithIcons: data?.filter(g => g.icon).length || 0,
      groupsByCompany: {} as { [compId: string]: number },
    };

    // Count by company
    data?.forEach(group => {
      const compId = group.comp_id || 'unknown';
      stats.groupsByCompany[compId] = (stats.groupsByCompany[compId] || 0) + 1;
    });

    return stats;
  }

  /**
   * Create a new report group
   */
  async createReportGroup(
    groupData: Omit<SupabaseReportGroup, 'id' | 'created_at' | 'updated_at' | 'synced_at'>
  ): Promise<SupabaseReportGroup> {
    const { data, error } = await supabase
      .from('report_groups')
      .insert([groupData])
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create report group: ${error.message}`);
    }

    return data;
  }

  /**
   * Update a report group
   */
  async updateReportGroup(
    id: string,
    updates: Partial<SupabaseReportGroup>
  ): Promise<SupabaseReportGroup> {
    const { data, error } = await supabase
      .from('report_groups')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update report group: ${error.message}`);
    }

    return data;
  }

  /**
   * Delete a report group
   */
  async deleteReportGroup(id: string): Promise<void> {
    const { error } = await supabase
      .from('report_groups')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(`Failed to delete report group: ${error.message}`);
    }
  }

  /**
   * Sync report groups from SQL Server
   */
  async syncReportGroupsFromSqlServer(): Promise<{
    synced: number;
    errors: number;
    message: string;
  }> {
    try {
      // This would typically call the sync script or API
      // For now, return a placeholder response
      return {
        synced: 0,
        errors: 0,
        message: 'Use npm run sync-report-groups to sync data',
      };
    } catch (error) {
      throw new Error(`Failed to sync report groups: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

export const reportGroupService = new ReportGroupService();